import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl/useCanonicalUrl'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getProjectMeta } from '@/services/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const numericList = 'list-decimal list-inside flex flex-col gap-4'

interface FreeTicketsProgramPageProps {
  projectTitle: string
  projectSlug: string
}

const FreeTicketsProgramPage: NextPageWithLayout<FreeTicketsProgramPageProps> = ({ projectTitle, projectSlug }) => {
  const freeTicketPath = paths.legal.freeTicketTerms(projectSlug)
  const canonical = useCanonicalUrl(freeTicketPath)
  const title = `Pay It Forward Tickets Terms | ${projectTitle} | Angel`

  return (
    <div className="bg-white py-20 text-justify text-black">
      <Seo
        canonical={canonical}
        title={title}
        description={`Terms and Conditions for the Angel Pay It Forward Tickets Program for ${projectTitle}.`}
        openGraph={{
          title,
          url: canonical,
          site_name: 'Angel',
        }}
        path={freeTicketPath}
      />
      <PaddedContainer>
        <div className="flex flex-col gap-4 p-10">
          <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p>TERMS AND CONDITIONS</p>
            <p>ANGEL STUDIOS PAY IT FORWARD TICKETS PROGRAM</p>
          </h1>

          <p className="text-center">Updated On: March 29, 2023</p>
          <p>
            These Terms and Conditions (these “Terms”) apply to your eligibility to receive a free movie ticket as part
            of the Pay it Forward Tickets Program (the “Program”) of Angel Studios, Inc. (“Angel Studios”). To receive a
            ticket, you must agree to these Terms.
          </p>
          <ol className={numericList}>
            <li>
              <b>Tickets.</b>{' '}
              {`By taking the actions described in Section 2 of these Terms, you may be eligible to
                receive a ticket to view “${projectTitle}” (a “Ticket”). Tickets are nontransferable and may not be
                redeemed or substituted for cash or any other form of value. Tickets are limited to one Ticket per
                person.`}
            </li>
            <li>
              <b>Eligibility.</b> To be eligible to receive a Ticket, you must have described the reason for requesting
              a Ticket, created an account with Angel Studios at <a href={paths.account.index}>www.angel.com/account</a>
              , and agreed to the terms and conditions relating to the creation of that account. You agree and
              acknowledge that you have taken the foregoing actions and that all information provided by you in
              connection with those actions is true and accurate.
            </li>
            <li>
              <b>Waitlist; Availability.</b> Upon accepting these Terms, you will be placed on a waitlist of people
              eligible to receive a Ticket. Tickets will be distributed on a first come, first serve basis. Being
              eligible to receive a Ticket does not grant you any right to receive a Ticket. Angel Studios has no
              obligation to provide you with a Ticket. Tickets are subject to availability. Further, Angel Studios may
              deny you a Ticket or revoke coupon codes in its sole and absolute discretion, including if it suspects
              that you are abusing the Program. Abuse of the Program includes, without limitation, the actual or
              attempted tampering with the operation of the Program, or the actual or attempted violation of these
              Terms. Angel Studios reserves the right to cancel or modify the Program if the Program is not operated as
              planned, including as a result of any technical, security or software issue.
            </li>
            <li>
              <b>Restrictions.</b> Tickets may be used at a limited number of theaters that have partnered with Angel
              Studios in providing the Program. Angel Studios does not guarantee that the theater and showtime you
              originally selected will be available when the Ticket is issued, and Angel Studios is not required to
              substitute your Ticket for a ticket usable at an alternative theater or showtime. Additionally, partner
              theaters may have specific terms for using the Ticket additional to these Terms, and you will be required
              to comply with all such third-party terms.
            </li>
            <li>
              <b>Additional Terms.</b> You agree and acknowledge that you have complied with and will continue to comply
              with the Angel Studios Terms of Use and Terms of Service.
            </li>
            <li>
              <b>Indemnification.</b> You agree to indemnify Angel Studios for any costs, damages and expenses
              (including reasonable attorney&apos;s fees) arising from or relating to any claim or cause of action
              brought by a third party against Angel Studios as a result of your participation in the Program, your use
              of the Ticket (including any actions or inactions taken at a partner theater), or your breach of these
              Terms, the Angel Studios Terms of Use, or the Angel Studios Terms of Service.
            </li>
            <li>
              <b>LIMITATION OF LIABILITY AND RELEASE.</b> By participating in the Pay it Forward Tickets Program,
              including through the receipt of a Ticket, you agree to the full extent permitted by applicable law to
              release and hold harmless Angel Studios and its affiliates and its and their officers, directors,
              employees, agents and representatives (the “Released Parties”) from and against any claim or cause of
              action of any kind, whether known or unknown, of any kind, arising from or relating to your participation
              in the Program, including without limitation your creation of an account with Angel Studios, your
              provision of personal information to Angel Studios, and your receipt and use of a Ticket. You further
              agree that in any such claim or cause of action, the liability of the Released Parties will be limited to
              your out-of-pocket costs of participating in the Program. In no event will the Released Parties be liable
              for attorneys’ fees or punitive, consequential, indirect or director damages whatsoever.
            </li>
            <li>
              <b>Choice of Law; Venue.</b> These Terms shall be governed by and construed according to the laws of the
              State of Utah, without regard to any conflict of laws rules that would cause the application of the laws
              of another jurisdiction. All claims, actions and disputes arising out of or relating to these Terms and
              the Program will be resolved exclusively by the courts located in Salt Lake County, Utah.
            </li>
            <li>
              <b>
                WAIVER OF CLASS ACTION. All claims, actions and disputes shall be brought individually and you hereby
                waive, to the maximum extent permissible under applicable law, any rights to bring or participate in a
                class action.
              </b>
            </li>
            <li>
              <b>Survival.</b> If, for any reason, any provision of these Terms is deemed invalid or unenforceable, such
              provisions shall be enforced to the maximum extent permitted by applicable law and the remaining
              provisions shall remain in full force and effect.
            </li>
            <li>
              <b>No Waiver by Angel Studios.</b> The failure by Angel Studios to enforce any provision or any rights
              provided under these Terms shall not constitute a waiver of such provision or rights. Any waiver is
              effective only if made in writing and signed by Angel Studios.
            </li>
            <li>
              <b>Amendment.</b>{' '}
              {`Angel Studios may amend these Terms at any time and for any reason by publishing the
              amended version of the Terms on its website at https://www.angel.com/legal/${projectSlug}/free-ticket-terms. Your continued
              participation in the Program will constitute your acceptance of the amended Terms.`}
            </li>
            <li>
              <b>Headings.</b> Headings used in these Terms shall not be used to interpret the contents herein.
            </li>
          </ol>
        </div>
      </PaddedContainer>
    </div>
  )
}

FreeTicketsProgramPage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<FreeTicketsProgramPageProps>(
  { filePath: 'pages/[locale]/legal/[projectSlug]/free-ticket-terms/index.tsx' },
  async ({ federationClient, locale, params, preview, next404 }) => {
    const projectSlug = getItemOrFirstEntry(params.projectSlug)
    if (!projectSlug) {
      return next404()
    }

    const [projectMeta, translations] = await Promise.all([
      getProjectMeta({ slug: projectSlug }, federationClient, { locale, preview }),
      loadTranslations(locale, ['common', 'account']),
    ])
    if (!projectMeta?.name) {
      return next404()
    }

    return {
      props: {
        projectTitle: projectMeta.name,
        projectSlug,
        ...translations,
      },
      revalidate: false,
    }
  },
)

export default FreeTicketsProgramPage
